import {
  Alert,
  Box,
  Button,
  Checkbox,
  DatePicker,
  Grid,
  GridItem,
  IconChevronRight,
  IconHouse,
  Input,
  Select,
  Typography,
  Spacer,
  IconPersonOutline,
  LoadingSpinner,
} from '@capitalise/ui-design-system';
import { useAuthContext } from 'HOC/withAuth';
import { useDirectors } from './useDirectors';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { ErrorCheckbox } from 'modules/AuthAlpha/components/CompanyForm/ErrorCheckbox';
import PropTypes from 'prop-types';
import { Analytics } from 'utils/Analytics';
import { AnalyticsEventAction, AnalyticsEventCategory } from 'enums/analytics-types';
import { UserType } from 'enums/user-type';
import { useEffect, useState } from 'react';
import { subtract } from 'utils/DateHelper';
import { useSnapshotGeneration } from 'services/sse/useSnapshotGeneration';
import { useTimeoutCheckpoints } from 'hooks/useTimeoutCheckpoints';
import {
  DirectorsFormLoadingStepLabels,
  DirectorsFormLoadingStepTimes,
} from 'modules/Business/shared/types';
import ConsentPurposeAlert from './ConsentPurposeAlert';

const maxDateOfBirth = subtract({ years: 16 });
const minDateOfBirth = subtract({ years: 120 });

const schema = yup.object({
  name: yup.string().required('This field is required'),
  dateOfBirth: yup
    .string()
    .typeError('This field is required')
    .test('Is > 16', 'You must be 16 years or older to be a director', (value) => {
      if (!value) return true;
      return new Date(value).getTime() <= maxDateOfBirth.getTime();
    })
    .test('Is < 120', 'Review the date of birth year is in the right format', (value) => {
      if (!value) return true;
      return new Date(value).getTime() >= minDateOfBirth.getTime();
    }),
  houseNumber: yup.string().required('This field is required'),
  street: yup.string().required('This field is required'),
  town: yup.string().required('This field is required'),
  postCode: yup.string().required('This field is required'),
  consent: yup
    .boolean()
    .oneOf([true], 'This field must be checked')
    .required('This field must be checked'),
});

const DirectorForm = ({
  onSubmit,
  isError,
  error,
  isLoading,
  isDirectorConsentFlow,
  buttonSize,
  directorData,
  origin = 'credit profile',
  prev,
}) => {
  const { user } = useAuthContext();
  const showErrorMessageOnSubmitButton = isError && !isDirectorConsentFlow;
  const [warningState, setWarningState] = useState({
    house: false,
    houseClosed: false,
    street: false,
    streetClosed: false,
  });
  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    watch,
    formState: { errors, isValid },
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema),
  });
  const watchHouseNumbber = watch('houseNumber', false);
  const watchStreet = watch('street', false);

  useEffect(() => {
    const isWarningShown = /\b(road|street|st|rd|place)\b/i.test(watchHouseNumbber);
    setWarningState((prevState) => ({
      ...prevState,
      house: isWarningShown && !prevState.houseClosed,
    }));
  }, [watchHouseNumbber]);

  useEffect(() => {
    const isStreetWarningShown = /\d+/.test(watchStreet);
    setWarningState((prevState) => ({
      ...prevState,
      street: isStreetWarningShown && !prevState.streetClosed,
    }));
  }, [watchStreet]);

  const {
    status,
    data: sseHookData,
    result,
  } = useSnapshotGeneration({
    entityId: user?.company?.company_id,
    checkFn: (experianData) => experianData?.companyDetails?.directors?.length > 0,
  });

  const { currentCheckpoint, startCounters, stopCounters } = useTimeoutCheckpoints(
    Object.values(DirectorsFormLoadingStepTimes),
  );

  const data = { result, experianData: sseHookData };

  const { directors } = useDirectors(data);
  const hasDirectors = directors?.length > 0;
  useEffect(() => {
    startCounters();
    sendAnalytic(AnalyticsEventAction.consentStart);
  }, []);

  useEffect(() => {
    if (hasDirectors) {
      stopCounters();
    }
  }, [hasDirectors]);

  const defaultDate = directorData?.dateOfBirth ? new Date(directorData?.dateOfBirth) : null;

  const [selectedDate, setSelectedDate] = useState(defaultDate);

  const sendData = (data) => {
    onSubmit(data);
  };

  useEffect(() => {
    if (isError) {
      sendAnalytic(
        isDirectorConsentFlow
          ? AnalyticsEventAction.consentError
          : AnalyticsEventAction.verifyError,
        error?.message,
      );
    }
  }, [isError]);

  const sendAnalytic = (event, error) => {
    if (!event) {
      return;
    }
    if (isDirectorConsentFlow) {
      return Analytics.track(AnalyticsEventCategory.business, event, {
        extraParams: {
          consentOrigin: origin,
        },
      });
    }
    Analytics.track(AnalyticsEventCategory.business, event, {
      extraParams: {
        userType: UserType.business,
        companyType: user?.company?.type,
        membership: user?.membership?.level?.name,
        creditScore: data?.experianData?.creditScore,
        employees: data?.experianData?.employees,
        sector: data?.experianData?.companyDetails?.normalizedSector,
        error,
      },
    });
  };

  const handleSkip = () => {
    sendAnalytic(AnalyticsEventAction.verifyDetails);
    sendAnalytic(AnalyticsEventAction.consentSkip);
    prev && prev();
  };

  if (!hasDirectors) {
    return (
      <Box display="flex" alignItems="center" flexDirection="column">
        <LoadingSpinner />
        <Typography size="body-16">
          {DirectorsFormLoadingStepLabels?.[currentCheckpoint] ?? 'Loading'}
        </Typography>
      </Box>
    );
  }

  return (
    <form onSubmit={handleSubmit(sendData)}>
      <Box display="flex">
        <IconPersonOutline />
        <Typography size="heading-18">Director&rsquo;s details</Typography>
      </Box>
      <Box marginTop="3xsmall" data-testid="box-directors-name">
        <Select
          label="Select a company director"
          listItems={directors}
          size="small"
          placeholder="Director name"
          onChange={(value) => {
            sendAnalytic(isDirectorConsentFlow ? undefined : AnalyticsEventAction.personalStart);
            setValue('name', value, { shouldValidate: true });
          }}
          defaultValue={directorData?.name}
          error={errors?.name?.message}
        />
      </Box>

      <Box marginTop="3xsmall">
        <DatePicker
          label="Director&rsquo;s date of birth"
          placeholder={'dd/mm/yyyy'}
          data-testid="input-birth-date"
          defaultValue={selectedDate}
          onChange={(value) => {
            if (value) {
              setSelectedDate(value);
              setValue('dateOfBirth', value, { shouldValidate: true });
              sendAnalytic(
                isDirectorConsentFlow
                  ? AnalyticsEventAction.consentPersonalComplete
                  : AnalyticsEventAction.personalComplete,
              );
            }
          }}
          error={errors.dateOfBirth?.message}
        />
      </Box>

      <Box backgroundColor="grey-50" height="1px" width="100%" my="medium" />
      <Box display="flex">
        <IconHouse />
        <Typography size="heading-18">Director&rsquo;s home address</Typography>
      </Box>
      <Box display="flex" flexDirection="column">
        <Box marginTop="3xsmall">
          <Input
            label="House number / name"
            fullWidth
            helperText="Enter the number of your house (i.e. street number), or if you stay in a house or building block just enter the name of the house or building block. Ignore any flat or apartment numbers."
            placeholder="E.g. 25 or Cherry Mansions"
            data-testid="input-houseNumber"
            {...register('houseNumber')}
            defaultValue={directorData?.houseNumber}
            onClick={() =>
              sendAnalytic(isDirectorConsentFlow ? undefined : AnalyticsEventAction.addressStart)
            }
            error={errors.houseNumber?.message}
          />
          {warningState.house && (
            <Box mt="tiny">
              <Alert
                closable
                onCloseClick={() => setWarningState({ ...warningState, houseClosed: true })}
                spacing="3xsmall"
                type="info"
              >
                <Typography color="grey-800" size="body-14">
                  It looks like you’ve entered a street or road name? Enter only a house number or
                  name.
                </Typography>
              </Alert>
            </Box>
          )}
        </Box>
        <Box marginTop="3xsmall">
          <Input
            label="Street"
            data-testid="input-street"
            fullWidth
            placeholder="E.g. Cherry Street"
            {...register('street')}
            error={errors.street?.message}
            defaultValue={directorData?.street}
            helperText="Only enter your street or road name here. Not the number."
          />
          {warningState.street && (
            <Box mt="tiny">
              <Alert
                closable
                onCloseClick={() => setWarningState({ ...warningState, streetClosed: true })}
                spacing="3xsmall"
                type="info"
              >
                <Typography color="grey-800" size="body-14">
                  It looks like you’ve entered a house number? Enter only the name of the street or
                  road.
                </Typography>
              </Alert>
            </Box>
          )}
        </Box>
        <Box marginTop="3xsmall">
          <Input
            label="Town"
            data-testid="input-town"
            fullWidth
            placeholder="E.g. London"
            {...register('town')}
            defaultValue={directorData?.town}
            error={errors.town?.message}
          />
        </Box>
        <Box marginTop="3xsmall">
          <Input
            label="Postcode"
            data-testid="input-postcode"
            fullWidth
            placeholder="E.g. SE1 8QH"
            {...register('postCode')}
            error={errors.postCode?.message}
            defaultValue={directorData?.postCode}
            onClick={() =>
              sendAnalytic(
                isDirectorConsentFlow
                  ? AnalyticsEventAction.consentAddressComplete
                  : AnalyticsEventAction.addressComplete,
              )
            }
          />
        </Box>
      </Box>
      <Box display="flex" marginTop="3xsmall">
        <Spacer
          gap="3xsmall"
          cursor="pointer"
          onClick={() => {
            const { consent } = getValues();

            sendAnalytic(
              isDirectorConsentFlow
                ? AnalyticsEventAction.consentVerify
                : AnalyticsEventAction.verifyDetails,
            );
            setValue('consent', !consent, { shouldValidate: true });
          }}
        >
          <Checkbox
            aria-label="consent"
            data-testid="checkbox-consent"
            name="consent"
            {...register('consent')}
          />
          <Typography size="body-16">
            I confirm I am a director of the company and consent for my details to be verified by
            Experian
          </Typography>
        </Spacer>
      </Box>
      <Box>
        {errors.consent?.message ? (
          <ErrorCheckbox data-testid="error-box-consent" error={errors.consent.message} />
        ) : null}
      </Box>
      <ConsentPurposeAlert marginBottom="3xlarge" marginTop="medium" />
      <Box
        position="fixed"
        bottom={0}
        left={0}
        backgroundColor="white"
        width="100%"
        paddingY="3xsmall"
        borderTop="1px solid"
        borderColor="grey-50"
        minHeight="3xlarge"
      >
        <Grid>
          <GridItem colSpan={4} />
          <GridItem colSpan={{ sm: 12, md: 12, default: 4 }}>
            <Box display="flex" gap="xtiny">
              <Button
                name="skip"
                type="button"
                baseColor="green"
                variant="outlined"
                size={buttonSize}
                data-testid="button-verify-later"
                fullWidth
                onClick={handleSkip}
              >
                Skip and verify later
              </Button>

              <Button
                name="submit"
                type="submit"
                baseColor="green"
                variant="filled"
                size={buttonSize}
                data-testid="button-submit"
                fullWidth
                rightIcon={<IconChevronRight />}
                loading={isLoading}
                disabled={!isValid}
              >
                Verify my details
              </Button>
            </Box>
            {showErrorMessageOnSubmitButton && <ErrorCheckbox error={error.message} />}
          </GridItem>
          <GridItem colSpan={4} />
        </Grid>
      </Box>
    </form>
  );
};

DirectorForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  isError: PropTypes.bool,
  error: PropTypes.object,
  isLoading: PropTypes.bool,
  isDirectorConsentFlow: PropTypes.bool,
  buttonSize: PropTypes.oneOf(['small', 'medium', 'large']),
  origin: PropTypes.string,
  directorData: PropTypes.object,
  prev: PropTypes.func,
};

DirectorForm.defaultProps = {
  buttonSize: 'large',
};

export default DirectorForm;
